import { useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import HeroSection from "./HeroSection";
import NFTsListingSection from "./NFTsListingSection";
import FeaturedCollectionSection from "./FeaturedCollectionSection";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
  getFeaturedCollections,
  getFeaturedNFTs,
  getFilteredNFTs,
  getTrendingCollection,
} from "../../slices/marketplace/marketplaceThunks";
import { formatToK } from "../../utils";
import RankedNFTListSection from "./RankedNFTListSection";
import { getRankedNFTsCollection } from "../../slices/aIRanking/aIRankingThunks";
import { RankedNFTsCollection } from "../../pages/Marketplace/Types";
import { Filters } from "./Types";

type NFTs = {
  id: string;
  nftImage: string;
  nftName: string;
  price: number;
  ecoScore: number;
  networkId: number | string;
};

type FeaturedCollections = {
  id: string;
  image: string;
  name: string;
  floorPrice: string | number;
  volume: string | number;
  chain: string;
};

type TrendingCollection = {
  _id: string;
  bannerImage: string;
  featureImage: string;
  collection: string;
  floorPrice: string;
  floorChange: string;
  volume: string;
  volumeChange: string;
  items: string;
  owners: string;
  sales?: string;
  name: string;
  volumes: string;
  chain: string;
  score: number | string;
};

interface Loading {
  rankedNFTsCollection: boolean;
  allNFTs: boolean;
  featuredCollections: boolean;
  trendingCollection: boolean;
}

interface Page {
  rankedNFTsCollection: number;
  allNFTs: number;
  featuredCollections: number;
  trendingCollection: number;
}

interface LoadingMore {
  rankedNFTsCollection: boolean;
  allNFTs: boolean;
  featuredCollections: boolean;
  trendingCollection: boolean;
}

const Marketplace = () => {
  const nftListingRef = useRef<HTMLDivElement>(null);
  const dispatch: AppDispatch = useDispatch();
  const [allNFTs, setAllNFTs] = useState<NFTs[] | []>([]);
  const [featuredNFTs, setFeaturedNFTs] = useState<NFTs[] | []>([]);
  const [featuredCollections, setFeaturedCollections] = useState<
    FeaturedCollections[] | []
  >([]);
  const [moreFeaCollections, setMoreFeaCollections] = useState<
    FeaturedCollections[] | []
  >([]);
  const [trendingCollectionRows, setTrendingCollectionRows] = useState<
    TrendingCollection[] | []
  >([]);
  const [isLoading, setIsLoading] = useState<Loading>({
    rankedNFTsCollection: false,
    allNFTs: false,
    featuredCollections: false,
    trendingCollection: false,
  });
  const [currentPage, setCurrentPage] = useState<Page>({
    rankedNFTsCollection: 1,
    allNFTs: 1,
    featuredCollections: 1,
    trendingCollection: 1,
  });
  const [totalPages, setTotalPages] = useState<Page>({
    rankedNFTsCollection: 1,
    allNFTs: 1,
    featuredCollections: 1,
    trendingCollection: 1,
  });
  const [isLoadingMore, setIsLoadingMore] = useState<LoadingMore>({
    rankedNFTsCollection: false,
    allNFTs: false,
    featuredCollections: false,
    trendingCollection: false,
  });

  const scrollToNFTListing = () => {
    if (nftListingRef.current) {
      const targetPosition = nftListingRef.current.offsetTop - 100; // (-100 for stopping 100px before)
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  const getNFTs = () => {
    dispatch(getFeaturedNFTs())
      .then((res: any) => {
        if (res?.error) {
          throw new Error(res?.payload);
        }
        console.log("res nfts", res);
        const mappedNFTs = res?.payload?.data?.data?.map((item: any) => ({
          id: item?._id,
          nftName: item?.name || "--",
          nftImage: item?.fileUrl || "--",
          price: item?.price || "0",
          ecoScore: item?.score || "0",
          networkId: item?.networkId || 1,
        }));
        setFeaturedNFTs(mappedNFTs);
      })
      .catch((err) => {
        console.error("Error fetching featured collections:", err.message);
      })
      .finally(() => {});
  };

  useEffect(() => {
    getFilteredNFTsList();
  }, [featuredNFTs]);

  const getFilteredNFTsList = (
    name: string = "",
    blockchain_network: string = "",
    rank_by: string = "",
    category_id: string = "",
    eco_category_id: string = "",
    limit: number = 10,
    pageNo: number = 1,
    isLoadingMore: boolean = false
  ) => {
    setIsLoading((prev: any) => ({ ...prev, allNFTs: !isLoadingMore }));
    setIsLoadingMore((prev: any) => ({ ...prev, allNFTs: isLoadingMore }));
    const network = blockchain_network === "all" ? "" : blockchain_network;
    const category = category_id === "all" ? "" : category_id;
    const ecoCategory = eco_category_id === "all" ? "" : eco_category_id;
    dispatch(
      getFilteredNFTs({
        limit,
        page: pageNo,
        name,
        blockchain_network: network,
        rank_by,
        category_id: category,
        eco_category_id: ecoCategory,
      })
    )
      .then((res: any) => {
        console.log("res filter", res);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setTotalPages((prev: any) => ({
          ...prev,
          allNFTs: res?.payload?.data?.meta?.totalPages,
        }));
        setCurrentPage((prev: any) => ({
          ...prev,
          allNFTs: res?.payload?.data?.meta?.currentPage,
        }));
        const mappedNFTs = res?.payload?.data?.data?.map((item: any) => ({
          id: item?._id,
          nftName: item?.name || "--",
          nftImage: item?.fileUrl || "--",
          price: item?.price || "0",
          ecoScore: item?.score || "0",
          networkId: item?.networkId || 1,
        }));
        const currentPage = res?.payload?.data?.meta?.currentPage;

        if (pageNo === 1 && !isLoadingMore) {
          setAllNFTs([...featuredNFTs]);
        } else if (isLoadingMore && currentPage > 1) {
          // Loading more NFTs (append data)
          setAllNFTs((prevRecords: any) => [...prevRecords, ...mappedNFTs]);
        } else if (
          !name &&
          !blockchain_network &&
          !rank_by &&
          !category_id &&
          !eco_category_id
        ) {
          // No filters applied (initial unfiltered load or reset)
          setAllNFTs((prevRecords: any) => [...prevRecords, ...mappedNFTs]);
        } else {
          // Filters applied (reset data)
          setAllNFTs(mappedNFTs);
        }
      })
      .catch((err) => {
        console.log({err})
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, allNFTs: false }));
        setIsLoadingMore((prev: any) => ({ ...prev, allNFTs: false }));
      });
  };

  const getFeaturedCollectionsList = (
    limit: number = 6,
    pageNo: number = 1,
    isLoadingMore: boolean = false
  ) => {
    setIsLoading((prev: any) => ({
      ...prev,
      featuredCollections: !isLoadingMore,
    }));
    setIsLoadingMore((prev: any) => ({
      ...prev,
      featuredCollections: isLoadingMore,
    }));
    dispatch(getFeaturedCollections({ limit, page: pageNo }))
      .then((res: any) => {
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setTotalPages((prev: any) => ({
          ...prev,
          featuredCollections: res?.payload?.data?.meta?.totalPages,
        }));
        setCurrentPage((prev: any) => ({
          ...prev,
          featuredCollections: res?.payload?.data?.meta?.currentPage,
        }));
        const mappedCollections = res?.payload?.data?.data?.map(
          (item: any) => ({
            id: item?._id,
            image: item?.featureImage,
            name: item?.name || "--",
            floorPrice: formatToK(item?.floorPrice) || "0.0",
            volume: formatToK(item?.volumes) || "0.0",
            chain: item?.chain.toLowerCase(),
          })
        );
        const arrangedCollections = [
          mappedCollections[4],
          mappedCollections[3],
          mappedCollections[5]
        ];
        setFeaturedCollections(arrangedCollections);
        setMoreFeaCollections([
          mappedCollections[2],
          mappedCollections[0],
          mappedCollections[1],
        ]);
      })
      .catch((err) => {
        console.error("Error fetching featured collections:", err.message);
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, featuredCollections: false }));
        setIsLoadingMore((prev: any) => ({
          ...prev,
          featuredCollections: false,
        }));
      });
  };

  const handleLoadMoreFeaCollections = () => {
    setFeaturedCollections((prev) => [...prev, ...moreFeaCollections]);
  };

  const getTrendingCollectionList = (
    page: number = 1,
    limit: number = 3,
    duration: string = "30D"
  ) => {
    setIsLoading((prev: any) => ({ ...prev, trendingCollection: true }));
    dispatch(getTrendingCollection({ page, limit, duration }))
      .then((res: any) => {
        console.log("res tre", res?.payload?.data?.meta);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setTotalPages((prev: any) => ({
          ...prev,
          trendingCollection: res?.payload?.data?.meta?.totalPages,
        }));
        const pageSize = 10;
        // Calculate the starting index for the serial number
        const startingIndex = (page - 1) * pageSize;
        const mappedCollectionRows = res?.payload?.data?.data?.map(
          (item: TrendingCollection, index: number) => ({
            sNo: startingIndex + index + 1,
            id: item?._id,
            bannerImage: item?.bannerImage,
            featureImage: item?.featureImage,
            collection: item?.name,
            floorPrice: item?.floorPrice,
            floorChange: item?.floorChange,
            volume: item?.volumes,
            volumeChange: item?.volumeChange,
            items: item?.items,
            owners: item?.owners,
            chain: item?.chain,
            score: item?.score
          })
        );
        setTrendingCollectionRows(mappedCollectionRows);
      })
      .catch((err) => {
        console.log({err})
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, trendingCollection: false }));
      });
  };

  const rankedNFTListRef = useRef<HTMLDivElement>(null);
  const [rankedNFTsCollectionRows, setRankedNFTsCollectionRows] = useState<
    RankedNFTsCollection[] | []
  >([]);
  const [filters, setFilters] = useState<Filters>({
    score: "",
    network: "",
    popularity: "",
    price: "",
  });
  const [rankedNFTsTotalPages, setRankedNFTsTotalPages] = useState(1);
  const [rankedNFTsCurrentPage, setRankedNFTsCurrentPage] = useState(1);

  useEffect(() => {
    getRankedNFTsCollectionList(
      rankedNFTsCurrentPage,
      10,
      filters?.score,
      filters?.network,
      filters?.popularity,
      filters?.price
    );
  }, [rankedNFTsCurrentPage]);

  const getRankedNFTsCollectionList = (
    page: number = 1,
    limit: number = 10,
    score: number | string = "",
    network: string | number = "",
    popularity: number | string = "",
    price: number | string = ""
  ) => {
    setIsLoading((prev: any) => ({ ...prev, rankedNFTsCollection: true }));
    const networkId = network === "all" ? "" : network;
    dispatch(
      getRankedNFTsCollection({
        page,
        limit,
        score,
        network: networkId,
        popularity,
        price,
      })
    )
      .then((res: any) => {
        console.log("res", res?.payload?.data?.data);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setRankedNFTsTotalPages(res?.payload?.data?.meta?.totalPages);

        const pageSize = 10;
        // Calculate the starting index for the serial number
        const startingIndex = (page - 1) * pageSize;
        const mappedCollectionRows = res?.payload?.data?.data?.map(
          (item: RankedNFTsCollection, index: number) => ({
            id: item?._id,
            sNo: startingIndex + index + 1,
            bannerImage: item?.bannerImage,
            featureImage: item?.featureImage,
            collection: item?.name,
            score: item?.score,
            floorPrice: item?.floorPrice,
            floorChange: item?.floorChange,
            volume: item?.volumes,
            volumeChange: item?.volumeChange,
            items: item?.items,
            owners: item?.owners,
            sales: item?.sales,
            chain: item?.chain,
          })
        );
        setRankedNFTsCollectionRows(mappedCollectionRows);
      })
      .catch((err) => {
        console.log({err})
      })
      .finally(() => {
        setIsLoading((prev: any) => ({ ...prev, rankedNFTsCollection: false }));
      });
  };

  useEffect(() => {
    getNFTs();
    getFeaturedCollectionsList();
    getTrendingCollectionList();
  }, []);

  return (
    <Layout fullScreen>
      <HeroSection
        onStartDiscovery={scrollToNFTListing}
        collectionDetails={"market-place"}
      />
      <RankedNFTListSection
        ref={rankedNFTListRef}
        currentPage={currentPage.rankedNFTsCollection}
        getFilteredNFTsCollection={getRankedNFTsCollectionList}
        rankedNFTsCollectionRows={rankedNFTsCollectionRows}
        filters={filters}
        setFilters={setFilters}
        isLoading={isLoading?.rankedNFTsCollection}
        pagination={{
          totalPages: rankedNFTsTotalPages,
          currentPage: rankedNFTsCurrentPage,
          setCurrentPage: setRankedNFTsCurrentPage,
        }}
      />
      <div ref={nftListingRef}>
        <NFTsListingSection
          isLoading={isLoading?.allNFTs}
          NFTs={allNFTs}
          currentPage={currentPage.allNFTs}
          totalPages={totalPages.allNFTs}
          getFilteredNFTs={getFilteredNFTsList}
          isLoadingMore={isLoadingMore.allNFTs}
        />
      </div>
      <FeaturedCollectionSection
        isLoading={isLoading?.featuredCollections}
        collections={featuredCollections}
        currentPage={currentPage.featuredCollections}
        totalPages={totalPages.featuredCollections}
        getMoreCollections={getFeaturedCollectionsList}
        isLoadingMore={isLoadingMore.featuredCollections}
        loadMoreFeaCollections={handleLoadMoreFeaCollections}
      />
    </Layout>
  );
};

export default Marketplace;
